

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './hooks/useToast';
import { AuthProvider } from './context/AuthContext';
import OfflineWarning from './components/common/OfflineWarning';
import { SubscriptionProvider } from './context/SubscriptionContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RegistrationSuccess from './components/auth/RegistrationSuccess';
import RegistrationCancel from './components/auth/RegistrationCancel';
import TeamManagement from './components/settings/TeamManagement';
import WarehouseManagement from './components/warehouses/WarehouseManagement';
import ReportsPage from './components/reports/ReportsPage';
import Subscribe from './pages/Subscribe';
import AnalyticsDashboard from './components/analytics/AnalyticsDashboard';
import ProductInsightsList from './components/analytics/ProductInsights/ProductInsightsList';
import CustomerInsightsList from './components/analytics/CustomerInsights/CustomerInsightsList';
// import AlertsList from './components/analytics/AnomalyAlerts/AlertsList';

// Auth Components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import LockScreen from './components/auth/LockScreen';

// Landing Page
import LandingPage from './components/landing/LandingPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Subscription Components
import LockoutScreen from './components/subscription/LockoutScreen';

// Main Components
import Dashboard from './components/dashboard/Dashboard';
import NavBar from './components/common/NavBar';

// Inventory Components
import InventoryList from './components/inventory/InventoryList';
import ProductForm from './components/inventory/ProductForm';
import BulkUpload from './components/inventory/BulkUpload';
import StockAdjustment from './components/inventory/StockAdjustment';
import StocksList from './components/inventory/StocksList';
import AddStock from './components/inventory/AddStock';
import StockTransfer from './components/inventory/StockTransfer';

// Orders Components
import OrdersList from './components/orders/OrdersList';
import OrderForm from './components/orders/OrderForm';
import OrderDetail from './components/orders/OrderDetail';

// Customers Components
import CustomersList from './components/customers/CustomersList';
import CustomerForm from './components/customers/CustomerForm';

// ⚠️ REMOVED: import WarehousesList - no longer needed

// Services
import syncService from './services/syncService';

import './App.css';

function App() {
  useEffect(() => {
    function handleOnline() {
      syncService.fullSync().then(result => {
        if (result.success) {
          const event = new CustomEvent('sync-completed', { detail: result });
          window.dispatchEvent(event);
        }
      });
    }

    function handleOffline() {
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <ToastProvider>
    <Router>
      <AuthProvider>
        <div className="app-container">
          <Routes>
            {/* ============ PUBLIC ROUTES ============ */}

            {/* Landing Page */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* Authentication */}
            <Route path="/register" element={<Register />} />
            <Route path="/registration/success" element={<RegistrationSuccess />} />
            <Route path="/registration/cancel" element={<RegistrationCancel />} />
            <Route path="/login" element={<Login />} />
            <Route path="/lock-screen" element={<LockScreen />} />
            <Route path="/lockout" element={<LockoutScreen />} />
            <Route path="/subscribe" element={<ProtectedRoute><Subscribe /></ProtectedRoute>} />

            {/* ============ PROTECTED ROUTES ============ */}

            {/* Dashboard */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <Dashboard />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* ============ TEAM MANAGEMENT ROUTE ============ */}

            {/* Team Management (Admin only) */}
            <Route path="/team" element={
              <ProtectedRoute feature="users" action="view">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <TeamManagement />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* ============ INVENTORY ROUTES ============ */}

            {/* Products List */}
            <Route path="/inventory" element={
              <ProtectedRoute feature="products" action="view">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <InventoryList />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* New Product */}
            <Route path="/inventory/new" element={
              <ProtectedRoute feature="products" action="create">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <ProductForm />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* Edit Product */}
            <Route path="/inventory/edit/:id" element={
              <ProtectedRoute feature="products" action="edit">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <ProductForm />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* Bulk Upload */}
            <Route path="/inventory/bulk-upload" element={
              <ProtectedRoute feature="products" action="create">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <BulkUpload />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* ============ STOCK ROUTES ============ */}

            {/* Stocks List */}
            <Route path="/inventory/stocks" element={
              <ProtectedRoute feature="stocks" action="view">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <StocksList />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* Add Stock */}
            <Route path="/inventory/stocks/new" element={
              <ProtectedRoute feature="stocks" action="create">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <AddStock />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* Stock Adjustment */}
            <Route path="/inventory/stock-adjustment/:productId" element={
              <ProtectedRoute feature="stocks" action="edit">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <StockAdjustment />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* Stock Transfer */}
            <Route path="/inventory/stock-transfer" element={
              <ProtectedRoute feature="stocks" action="edit">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <StockTransfer />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* ============ ORDERS ROUTES ============ */}

            {/* Orders List */}
            <Route path="/orders" element={
              <ProtectedRoute feature="orders" action="view">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <OrdersList />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* New Order */}
<Route path="/orders/new" element={
  <ProtectedRoute feature="orders" action="create">
    <SubscriptionProvider>
      <NavBar />
      <div className="main-content">
        <OrderForm />
      </div>
    </SubscriptionProvider>
  </ProtectedRoute>
} />

{/* Edit Order */}
<Route path="/orders/edit/:id" element={
  <ProtectedRoute feature="orders" action="edit">
    <SubscriptionProvider>
      <NavBar />
      <div className="main-content">
        <OrderForm />
      </div>
    </SubscriptionProvider>
  </ProtectedRoute>
} />

{/* Order Detail */}
<Route path="/orders/:id" element={
  <ProtectedRoute feature="orders" action="view">
    <SubscriptionProvider>
      <NavBar />
      <div className="main-content">
        <OrderDetail />
      </div>
    </SubscriptionProvider>
  </ProtectedRoute>
} />

            {/* ============ CUSTOMERS ROUTES ============ */}

            {/* Customers List */}
            <Route path="/customers" element={
              <ProtectedRoute feature="customers" action="view">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <CustomersList />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* New Customer */}
            <Route path="/customers/new" element={
              <ProtectedRoute feature="customers" action="create">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <CustomerForm />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* Edit Customer */}
            <Route path="/customers/edit/:id" element={
              <ProtectedRoute feature="customers" action="edit">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <CustomerForm />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

            {/* ============ WAREHOUSES ROUTES ============ */}

            {/* Warehouses Management - FIXED: Only one route now! */}
            <Route path="/warehouses" element={
              <ProtectedRoute feature="warehouses" action="view">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <WarehouseManagement />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />


            <Route path="/reports" element={
              <ProtectedRoute feature="reports" action="view">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <ReportsPage />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
              } />
            {/* ============ ANALYTICS ROUTES ============ */}
            
            <Route path="/analytics" element={
              <ProtectedRoute feature="analytics" action="view">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <AnalyticsDashboard />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />
            
            <Route path="/analytics/products" element={
              <ProtectedRoute feature="analytics" action="view">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <ProductInsightsList />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />

          <Route path="/analytics/customers" element={
              <ProtectedRoute feature="analytics" action="view">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <CustomerInsightsList />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />
            
{/* <Route path="/analytics/anomalies" element={
              <ProtectedRoute feature="analytics" action="view">
                <SubscriptionProvider>
                  <NavBar />
                  <div className="main-content">
                    <AlertsList />
                  </div>
                </SubscriptionProvider>
              </ProtectedRoute>
            } />*/}


            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        <OfflineWarning />
      </AuthProvider>
    </Router>
  </ToastProvider>
  );
}

export default App;
